import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CapitoliActions } from './capitoli.actions';
import { CapitoliPort } from '../../core/services/capitoli/capitoli.port';
import {catchError, map, mergeMap, of, switchMap, tap} from 'rxjs';
import { PreventiviActions } from '../preventivi/preventivi.actions';
import { SwalService } from '../../core/services/alerts/swal.service';
import {CantieriActions} from '../cantieri/cantieri.actions';

@Injectable()
export class CapitoliEffects {
  private actions$ = inject(Actions);
  private api = inject(CapitoliPort);
  private swalService = inject(SwalService);

  loadByPreventivoId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.loadByPreventivoId),
      switchMap(({ idPreventivo }) =>
        this.api.getByPreventivoId(idPreventivo).pipe(
          map((items) => CapitoliActions.loadByPreventivoIdSuccess({ items })),
          catchError((err) =>
            of(CapitoliActions.loadByPreventivoIdFailure({ error: err?.message ?? 'Errore caricamento' }))
          )
        )
      )
    )
  );

  loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.loadDetails),
      switchMap(({ id }) =>
        this.api.getById(id).pipe(
          map((item) => CapitoliActions.loadDetailsSuccess({ item })),
          catchError((err) =>
            of(CapitoliActions.loadDetailsFailure({ error: err?.message ?? 'Errore dettagli' }))
          )
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.create),
      switchMap(({ dto }) =>
        this.api.create(dto).pipe(
          map((id) => CapitoliActions.createSuccess({ id: Number(id), idPreventivo: dto.idPreventivo })),
          catchError((err) =>
            of(CapitoliActions.createFailure({ error: err?.message ?? 'Errore creazione' }))
          )
        )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.update),
      switchMap(({ id, idPreventivo, changes, updateVoci }) =>
        this.api.update(id, changes, updateVoci).pipe(
          map((returnedId) => CapitoliActions.updateSuccess({ id: Number(returnedId), idPreventivo })),
          catchError((err) =>
            of(CapitoliActions.updateFailure({ error: err?.message ?? 'Errore modifica' }))
          )
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.delete),
      switchMap(({ id, idPreventivo }) =>
        this.api.delete(id).pipe(
          map(() => CapitoliActions.deleteSuccess({ id, idPreventivo })),
          catchError((err) =>
            of(CapitoliActions.deleteFailure({ error: err?.message ?? 'Errore eliminazione' }))
          )
        )
      )
    )
  );

  refreshPreventivoDetailsAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.createSuccess, CapitoliActions.updateSuccess, CapitoliActions.deleteSuccess),
      map(({ idPreventivo }) => PreventiviActions.loadDetails({ id: idPreventivo }))
    )
  );

  showMutationSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CapitoliActions.createSuccess, CapitoliActions.updateSuccess, CapitoliActions.deleteSuccess),
        tap((action) => {
          switch (action.type) {
            case CapitoliActions.createSuccess.type:
              this.swalService.success('Capitolo creato con successo!');
              return;
            case CapitoliActions.updateSuccess.type:
              this.swalService.success('Capitolo aggiornato con successo!');
              return;
            case CapitoliActions.deleteSuccess.type:
              this.swalService.success('Capitolo eliminato con successo!');
              return;
          }
        })
      ),
    { dispatch: false }
  );

  showMutationFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CapitoliActions.createFailure, CapitoliActions.updateFailure, CapitoliActions.deleteFailure),
        tap(({ error }) => this.swalService.error(error || 'Operazione capitolo non riuscita'))
      ),
    { dispatch: false }
  );

  loadCantiereAndamentiByCantiereId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.loadByCantiereAndamentoId),
      switchMap(({ idCantiere }) =>
        this.api.getCantiereAndamentiByCantiereId(idCantiere).pipe(
          map((items) =>
            CapitoliActions.loadByCantiereAndamentoIdSuccess({ items })
          ),
          catchError((err) =>
            of(
              CapitoliActions.loadByCantiereAndamentoIdFailure({
                error: err?.message ?? 'Errore caricamento andamenti',
              })
            )
          )
        )
      )
    )
  );

  createCantiereAndamento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.createCantiereAndamento),
      switchMap(({ dto }) =>
        this.api.createCantiereAndamento(dto).pipe(
          map(() =>
            CapitoliActions.createCantiereAndamentoSuccess({
              idCantiere: dto.idCantiere,
            })
          ),
          catchError((err) =>
            of(
              CapitoliActions.createCantiereAndamentoFailure({
                error: err?.message ?? 'Errore creazione andamento',
              })
            )
          )
        )
      )
    )
  );

  updateCantiereAndamento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.updateCantiereAndamento),
      switchMap(({ id, idCantiere, changes }) =>
        this.api.updateCantiereAndamento(id, changes).pipe(
          map(() =>
            CapitoliActions.updateCantiereAndamentoSuccess({ idCantiere })
          ),
          catchError((err) =>
            of(
              CapitoliActions.updateCantiereAndamentoFailure({
                error: err?.message ?? 'Errore modifica andamento',
              })
            )
          )
        )
      )
    )
  );

  deleteCantiereAndamento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.deleteCantiereAndamento),
      switchMap(({ id, idCantiere }) =>
        this.api.deleteCantiereAndamento(id).pipe(
          map(() =>
            CapitoliActions.deleteCantiereAndamentoSuccess({
              id,
              idCantiere,
            })
          ),
          catchError((err) =>
            of(
              CapitoliActions.deleteCantiereAndamentoFailure({
                error: err?.message ?? 'Errore eliminazione andamento',
              })
            )
          )
        )
      )
    )
  );

  refreshCantiereAndamentiAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CapitoliActions.createCantiereAndamentoSuccess,
        CapitoliActions.updateCantiereAndamentoSuccess,
        CapitoliActions.deleteCantiereAndamentoSuccess
      ),
      map(({ idCantiere }) =>
        CapitoliActions.loadByCantiereAndamentoId({ idCantiere })
      )
    )
  );

  showCantiereAndamentoSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CapitoliActions.createCantiereAndamentoSuccess,
          CapitoliActions.updateCantiereAndamentoSuccess,
          CapitoliActions.deleteCantiereAndamentoSuccess
        ),
        tap((action) => {
          switch (action.type) {
            case CapitoliActions.createCantiereAndamentoSuccess.type:
              this.swalService.success('Andamento creato con successo!');
              return;
            case CapitoliActions.updateCantiereAndamentoSuccess.type:
              this.swalService.success('Andamento aggiornato con successo!');
              return;
            case CapitoliActions.deleteCantiereAndamentoSuccess.type:
              this.swalService.success('Andamento eliminato con successo!');
              return;
          }
        })
      ),
    { dispatch: false }
  );

  showCantiereAndamentoFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CapitoliActions.createCantiereAndamentoFailure,
          CapitoliActions.updateCantiereAndamentoFailure,
          CapitoliActions.deleteCantiereAndamentoFailure,
          CapitoliActions.loadByCantiereAndamentoIdFailure
        ),
        tap(({ error }) =>
          this.swalService.error(error || 'Operazione andamento non riuscita')
        )
      ),
    { dispatch: false }
  );

  loadExtrasByCantiereId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.loadExtraByCantiereId),
      switchMap(({ idCantiere }) =>
        this.api.getExtrasByCantiereId(idCantiere).pipe(
          map((items) => CapitoliActions.loadExtraByCantiereIdSuccess({ items })),
          catchError((err) =>
            of(CapitoliActions.loadExtraByCantiereIdFailure({
              error: err?.message ?? 'Errore caricamento extra',
            }))
          )
        )
      )
    )
  );

  createExtra$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.createExtra),
      switchMap(({ dto }) =>
        this.api.createExtra(dto).pipe(
          map(() => CapitoliActions.createExtraSuccess({ idCantiere: dto.idCantiere })),
          catchError((err) =>
            of(CapitoliActions.createExtraFailure({
              error: err?.message ?? 'Errore creazione extra',
            }))
          )
        )
      )
    )
  );

  updateExtra$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.updateExtra),
      switchMap(({ id, idCantiere, changes }) =>
        this.api.updateExtra(id, changes).pipe(
          map(() => CapitoliActions.updateExtraSuccess({ idCantiere })),
          catchError((err) =>
            of(CapitoliActions.updateExtraFailure({
              error: err?.message ?? 'Errore modifica extra',
            }))
          )
        )
      )
    )
  );

  deleteExtra$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.deleteExtra),
      switchMap(({ id, idCantiere }) =>
        this.api.deleteExtra(id).pipe(
          map(() => CapitoliActions.deleteExtraSuccess({ idCantiere })),
          catchError((err) =>
            of(CapitoliActions.deleteExtraFailure({
              error: err?.message ?? 'Errore eliminazione extra',
            }))
          )
        )
      )
    )
  );

  refreshExtrasAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CapitoliActions.createExtraSuccess,
        CapitoliActions.updateExtraSuccess,
        CapitoliActions.deleteExtraSuccess
      ),
      map(({ idCantiere }) => CapitoliActions.loadExtraByCantiereId({ idCantiere }))
    )
  );

  loadIncassiByCantiereId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.loadIncassiByCantiereId),
      switchMap(({ idCantiere }) =>
        this.api.getIncassiByCantiereId(idCantiere).pipe(
          map((items) => CapitoliActions.loadIncassiByCantiereIdSuccess({ items })),
          catchError((err) =>
            of(CapitoliActions.loadIncassiByCantiereIdFailure({
              error: err?.message ?? 'Errore caricamento incassi',
            }))
          )
        )
      )
    )
  );

  createIncasso$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.createIncasso),
      switchMap(({ dto }) =>
        this.api.createIncasso(dto).pipe(
          map(() => CapitoliActions.createIncassoSuccess({ idCantiere: dto.idCantiere })),
          catchError((err) =>
            of(CapitoliActions.createIncassoFailure({
              error: err?.message ?? 'Errore creazione incasso',
            }))
          )
        )
      )
    )
  );

  updateIncasso$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.updateIncasso),
      switchMap(({ id, idCantiere, changes }) =>
        this.api.updateIncasso(id, changes).pipe(
          map(() => CapitoliActions.updateIncassoSuccess({ idCantiere })),
          catchError((err) =>
            of(CapitoliActions.updateIncassoFailure({
              error: err?.message ?? 'Errore modifica incasso',
            }))
          )
        )
      )
    )
  );

  deleteIncasso$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.deleteIncasso),
      switchMap(({ id, idCantiere }) =>
        this.api.deleteIncasso(id).pipe(
          map(() => CapitoliActions.deleteIncassoSuccess({ idCantiere })),
          catchError((err) =>
            of(CapitoliActions.deleteIncassoFailure({
              error: err?.message ?? 'Errore eliminazione incasso',
            }))
          )
        )
      )
    )
  );

  refreshIncassiAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CapitoliActions.createIncassoSuccess,
        CapitoliActions.updateIncassoSuccess,
        CapitoliActions.deleteIncassoSuccess
      ),
      map(({ idCantiere }) => CapitoliActions.loadIncassiByCantiereId({ idCantiere }))
    )
  );

  reloadGraficiAfterExtraCrud$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CapitoliActions.createExtraSuccess,
        CapitoliActions.updateExtraSuccess,
        CapitoliActions.deleteExtraSuccess
      ),
      mergeMap(({ idCantiere }) => [
        CantieriActions.loadPieStats({ idCantiere }),
        CantieriActions.loadFlussoMensileStats({ idCantiere }),
      ])
    )
  );

  reloadAfterIncassoCrud$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CapitoliActions.createIncassoSuccess,
        CapitoliActions.updateIncassoSuccess,
        CapitoliActions.deleteIncassoSuccess
      ),
      mergeMap(({ idCantiere }) => [
        CapitoliActions.loadIncassiByCantiereId({ idCantiere }),

        CantieriActions.loadPieStats({ idCantiere }),
        CantieriActions.loadFlussoMensileStats({ idCantiere }),
      ])
    )
  );

  showExtraIncassoSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CapitoliActions.createExtraSuccess,
          CapitoliActions.updateExtraSuccess,
          CapitoliActions.deleteExtraSuccess,
          CapitoliActions.createIncassoSuccess,
          CapitoliActions.updateIncassoSuccess,
          CapitoliActions.deleteIncassoSuccess
        ),
        tap((action) => {
          switch (action.type) {
            case CapitoliActions.createExtraSuccess.type:
              this.swalService.success('Extra creato con successo!');
              return;
            case CapitoliActions.updateExtraSuccess.type:
              this.swalService.success('Extra aggiornato con successo!');
              return;
            case CapitoliActions.deleteExtraSuccess.type:
              this.swalService.success('Extra eliminato con successo!');
              return;
            case CapitoliActions.createIncassoSuccess.type:
              this.swalService.success('Incasso creato con successo!');
              return;
            case CapitoliActions.updateIncassoSuccess.type:
              this.swalService.success('Incasso aggiornato con successo!');
              return;
            case CapitoliActions.deleteIncassoSuccess.type:
              this.swalService.success('Incasso eliminato con successo!');
              return;
          }
        })
      ),
    { dispatch: false }
  );
  showExtraIncassoFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CapitoliActions.loadExtraByCantiereIdFailure,
          CapitoliActions.createExtraFailure,
          CapitoliActions.updateExtraFailure,
          CapitoliActions.deleteExtraFailure,
          CapitoliActions.loadIncassiByCantiereIdFailure,
          CapitoliActions.createIncassoFailure,
          CapitoliActions.updateIncassoFailure,
          CapitoliActions.deleteIncassoFailure
        ),
        tap(({ error }) => this.swalService.error(error || 'Operazione non riuscita'))
      ),
    { dispatch: false }
  );

  setExtraStato$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.setExtraStato),
      switchMap(({ id, idCantiere, stato, createIncasso }) =>
        this.api.setExtraStato(id, stato, createIncasso).pipe(
          map(() =>
            CapitoliActions.setExtraStatoSuccess({ idCantiere })
          ),
          catchError(err =>
            of(CapitoliActions.setExtraStatoFailure({
              error: err?.message ?? 'Errore aggiornamento stato extra',
            }))
          )
        )
      )
    )
  );

  reloadAfterSetExtraStato$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapitoliActions.setExtraStatoSuccess),
      map(({ idCantiere }) =>
        CapitoliActions.loadExtraByCantiereId({ idCantiere })
      )
    )
  );
}
